import React, { useMemo } from 'react'
import { format, getDay, getHours } from 'date-fns'

const TradingHeatmap = ({ trades }) => {
  const heatmapData = useMemo(() => {
    const data = Array(7).fill(null).map(() => Array(24).fill(0))
    const counts = Array(7).fill(null).map(() => Array(24).fill(0))

    trades.forEach(trade => {
      if (trade.entry_time && trade.pnl !== null) {
        const date = new Date(trade.entry_time)
        const day = getDay(date) // 0 = Sunday, 1 = Monday, etc.
        const hour = getHours(date)
        
        data[day][hour] += trade.pnl || 0
        counts[day][hour] += 1
      }
    })

    // Calculate average P&L per hour
    for (let day = 0; day < 7; day++) {
      for (let hour = 0; hour < 24; hour++) {
        if (counts[day][hour] > 0) {
          data[day][hour] = data[day][hour] / counts[day][hour]
        }
      }
    }

    return { data, counts }
  }, [trades])

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const maxValue = Math.max(...heatmapData.data.flat().map(Math.abs))

  const getCellColor = (value, count) => {
    if (count === 0) return 'bg-gray-100'
    
    const intensity = Math.abs(value) / maxValue
    if (value > 0) {
      return `bg-green-${Math.min(Math.ceil(intensity * 5) * 100, 500)}`
    } else {
      return `bg-red-${Math.min(Math.ceil(intensity * 5) * 100, 500)}`
    }
  }

  const getCellOpacity = (value, count) => {
    if (count === 0) return 0.1
    const intensity = Math.abs(value) / maxValue
    return Math.max(0.2, intensity)
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Trading Activity Heatmap</h3>
      
      {trades.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No trading data available for heatmap</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-max">
            {/* Hour labels */}
            <div className="flex mb-2">
              <div className="w-12"></div>
              {Array.from({ length: 24 }, (_, i) => (
                <div key={i} className="w-6 text-xs text-gray-500 text-center">
                  {i}
                </div>
              ))}
            </div>

            {/* Heatmap grid */}
            {dayLabels.map((day, dayIndex) => (
              <div key={day} className="flex items-center mb-1">
                <div className="w-12 text-sm font-medium text-gray-700">
                  {day}
                </div>
                {Array.from({ length: 24 }, (_, hour) => {
                  const value = heatmapData.data[dayIndex][hour]
                  const count = heatmapData.counts[dayIndex][hour]
                  const opacity = getCellOpacity(value, count)
                  
                  return (
                    <div
                      key={hour}
                      className="w-6 h-6 mx-0.5 rounded-sm cursor-pointer hover:ring-2 hover:ring-blue-300 transition-all"
                      style={{
                        backgroundColor: value > 0 ? `rgba(34, 197, 94, ${opacity})` :
                                       value < 0 ? `rgba(239, 68, 68, ${opacity})` :
                                       'rgba(156, 163, 175, 0.1)'
                      }}
                      title={`${day} ${hour}:00 - Avg P&L: $${value.toFixed(2)} (${count} trades)`}
                    />
                  )
                })}
              </div>
            ))}

            {/* Legend */}
            <div className="flex items-center justify-center mt-6 space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-red-400 rounded-sm"></div>
                <span className="text-sm text-gray-600">Losses</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-gray-200 rounded-sm"></div>
                <span className="text-sm text-gray-600">No trades</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-green-400 rounded-sm"></div>
                <span className="text-sm text-gray-600">Profits</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TradingHeatmap